import { useState, useRef, useCallback, useEffect } from 'react';
import { useChatContext } from '../contexts/ChatContext';

/**
 * 채팅 UI 로직 훅: ChatContext의 공유 상태 + 로컬 스크롤/UI 상태를 결합.
 * ChatSidePanel, ProjectChatPage 등 각 소비자별로 독립적인 스크롤 관리.
 */
export function useChatUI() {
  const ctx = useChatContext();

  const [showNewMsgBtn, setShowNewMsgBtn] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const prevLastMsgIdRef = useRef<string | null>(null);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior });
    }
  }, []);

  // 초기 로드 완료 시 스크롤 (채널 변경 또는 메시지 최초 로드 시)
  useEffect(() => {
    if (!ctx.isLoading && ctx.messages.length > 0) {
      scrollToBottom();
      requestAnimationFrame(() => scrollToBottom());
      const timer = setTimeout(() => scrollToBottom(), 100);
      prevLastMsgIdRef.current = ctx.messages[ctx.messages.length - 1]?.messageId ?? null;
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [ctx.isLoading, ctx.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 새 메시지 추가 시 (React 렌더 후) 자동 스크롤 + 읽음 처리
  useEffect(() => {
    if (ctx.isLoading || ctx.messages.length === 0) return;
    const lastMsgId = ctx.messages[ctx.messages.length - 1]?.messageId;
    if (lastMsgId && lastMsgId !== prevLastMsgIdRef.current) {
      prevLastMsgIdRef.current = lastMsgId;
      requestAnimationFrame(() => scrollToBottom('smooth'));
      ctx.markAsRead();
      setShowNewMsgBtn(false);
    }
  }, [ctx.messages, ctx.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScrollChange = useCallback((atBottom: boolean) => {
    isAtBottomRef.current = atBottom;
    if (atBottom) {
      setShowNewMsgBtn(false);
    }
  }, []);

  const handleNewMsgBtnClick = useCallback(() => {
    setShowNewMsgBtn(false);
    scrollToBottom('smooth');
    ctx.markAsRead();
  }, [scrollToBottom, ctx]);

  return {
    ...ctx,
    showNewMsgBtn,
    handleNewMsgBtnClick,
    handleScrollChange,
    scrollContainerRef,
    messagesEndRef,
  };
}
